import { t } from 'i18next';
import * as yup from 'yup';

import { ImmerGet, ImmerSet } from '../types';

import { Bot, EditableBotVersionSlice } from '@/domain/bot';
import { BotMappieNewVersion } from '@/domain/bot-mappie';
import { botSchema } from '@/schemas/bot-validation-schema';
import { getBotRaw, getBotVersion, patchBotVersion } from '@/services/api/bots';
import {
    isCustomCxdBot,
    isProgrammaticBot,
    pushUndoAction,
    resetUndoStack,
} from 'utils/bot/state/editable-bot-fns';
import { getModuleTypeHandlers } from 'utils/bot/transformation/graph-data-to-modules';
import { transformBotMappieForPortal } from 'utils/bot/transformation/transform-bot-for-frontend';
import { transformBotToMappieVersion } from 'utils/bot/transformation/transform-bot-for-mappie';
import { setupCustomModuleHandles } from 'utils/reactflow/';
import {
    getLayoutedElements,
    nodeHeight,
    nodeWidth,
} from 'utils/reactflow/nodes/get-layouted-elements';

/** Bot version CRUD. custom-cxd: load/validate via `load-custom-cxd-version`; save on version page only. */
export const createBotVersionCrudActions = (
    set: ImmerSet,
    get: ImmerGet,
): Pick<
    EditableBotVersionSlice,
    | 'fetchBotCurrentVersion'
    | 'fetchBotVersion'
    | 'getNewBotVersion'
    | 'validateBot'
    | 'saveBotVersion'
    | 'getMappiePayloadForNewVersion'
    | 'markSaveSuccess'
> => ({
    fetchBotCurrentVersion: async (botId: string, accountId?: string) => {
        try {
            const botDetail = await getBotRaw(botId, accountId);
            set((state) => {
                state.currentBotVersion =
                    botDetail.current_bot_version_id ?? '';
            });
            return botDetail.current_bot_version_id ?? '';
        } catch (e) {
            console.log(e);
        }
    },

    fetchBotVersion: async (
        botId: string,
        versionId: string,
        accountId?: string,
    ) => {
        set((state) => {
            state.isLoadingBotVersion = true;
        });

        try {
            const botMappieVersion = await getBotVersion(
                botId,
                versionId,
                accountId,
            );

            if (
                isProgrammaticBot(botMappieVersion.labels) &&
                botMappieVersion.dialog
            ) {
                get().setBotMappie(botMappieVersion);
            }

            const botVersion = transformBotMappieForPortal(botMappieVersion);

            if (isCustomCxdBot(botVersion.labels)) {
                // custom-cxd graph — `load-custom-cxd-version.ts`
                const { loadCustomCxdReactFlow } = await import(
                    '@/components/dynamic-flow-editor/load-custom-cxd-version'
                );
                const { nodes: editorNodes, edges: editorEdges } =
                    await loadCustomCxdReactFlow(botMappieVersion, accountId);
                botVersion.nodesReactFlow = editorNodes as any;
                botVersion.edgesReactFlow = editorEdges as any;
            }

            const nodes = botVersion.nodesReactFlow;
            const edges = botVersion.edgesReactFlow;

            setupCustomModuleHandles(
                nodes,
                edges,
                botMappieVersion,
                (nodeId) => get().customModuleNodeHandles[nodeId] || [],
                get().setCustomModuleNodeHandles,
            );

            if (
                nodes.every(
                    (node) => node.position.x == 0 && node.position.y == 0,
                )
            ) {
                // node positions are not loaded, perform auto layout
                const dagreGraph = getLayoutedElements(nodes, edges);

                nodes.forEach((node, index) => {
                    nodes[index].position = {
                        x: dagreGraph.node(node.id).x - nodeWidth / 2,
                        y: dagreGraph.node(node.id).y - nodeHeight / 2,
                    };
                });
            }
            set((state) => {
                const bot = state.editingBot;
                bot.name = botVersion.name;
                bot.description = botVersion.description;
                bot.botId = botVersion.botId;
                bot.botVersionId = botVersion.botVersionId;
                bot.voiceConfig = botVersion.voiceConfig;
                bot.smsConfig = botVersion.smsConfig;
                bot.language = botVersion.language;
                bot.channel = botVersion.channel;
                bot.edgesReactFlow = edges;
                bot.nodesReactFlow = nodes;
                bot.taskValues = botVersion.taskValues;
                bot.prompt_texts = botVersion.prompt_texts;
                bot.intent_texts = botVersion.intent_texts;
                bot.entity_texts = botVersion.entity_texts;
                bot.mchannelsBotId = botVersion.mchannelsBotId;
                bot.labels = botVersion.labels;
                bot.stickers = botVersion.stickers;
                state.addedNodePosition = state.addedNodePosition;
                state.isLoadingBotVersion = false;
                state.errorFetchBotVersion = undefined;
                resetUndoStack(state, get);
            });
        } catch (e) {
            set((state) => {
                state.isLoadingBotVersion = false;
                state.errorFetchBotVersion = {
                    isErrorFetchBotVersion: true,
                    errorMessageFetchBot: e,
                    errorMessageFetchBotKey: `get_bot_version_${botId}_${versionId}`,
                };
            });
        }
    },

    getNewBotVersion: (bot?: Bot) => {
        const currentBot = bot ?? get().editingBot;
        return {
            name: currentBot.name,
            description: currentBot.description,
            voiceConfig: currentBot.voiceConfig,
            smsConfig: currentBot.smsConfig,
            language: currentBot.language,
            channel: currentBot.channel,
            edgesReactFlow: currentBot.edgesReactFlow,
            selectedHandle: currentBot.selectedHandle,
            nodesReactFlow: currentBot.nodesReactFlow,
            stickers: currentBot.stickers,
            taskValues: currentBot.taskValues,
            prompt_texts: currentBot.prompt_texts,
            intent_texts: currentBot.intent_texts,
            entity_texts: currentBot.entity_texts,
            mchannelsBotId: currentBot.mchannelsBotId,
        };
    },

    validateBot: async (bot?: Bot) => {
        const bS = botSchema(t);
        const newBot = get().getNewBotVersion(bot);
        await bS.validate(newBot, {
            abortEarly: false,
        });

        const isCustomCxd = isCustomCxdBot((bot ?? get().editingBot).labels);
        // custom-cxd: dynamic import for handler check
        const brokenHandlers: string[] = isCustomCxd
            ? await import(
                  '@/components/dynamic-flow-editor/load-custom-cxd-version'
              ).then(({ findBrokenCustomCxdHandlers }) =>
                  findBrokenCustomCxdHandlers(newBot),
              )
            : [];

        if (!isCustomCxd) {
            for (const node of newBot.nodesReactFlow) {
                const nodeName = node.id;
                const nodeType = node.type?.toLowerCase().endsWith('node')
                    ? node.type.slice(0, -'node'.length)
                    : node.type;

                // this fn below needs to be adjusted for Y/N module to take into account both 3 and 4 outputs possibilty
                const expectedNumOfEdges = getModuleTypeHandlers(
                    nodeType,
                    node,
                    get().customModuleNodeHandles,
                ).length;
                const actualNumOfEdges = newBot.edgesReactFlow.reduce(
                    (acc: number, edge: { source: any }) =>
                        acc + (edge.source === nodeName ? 1 : 0),
                    0,
                );

                if (expectedNumOfEdges !== actualNumOfEdges) {
                    brokenHandlers.push(nodeName);
                }
            }
        }

        if (brokenHandlers.length > 0) {
            throw new yup.ValidationError(`${brokenHandlers}`);
        }
    },

    getMappiePayloadForNewVersion: () => {
        const bot = get().editingBot;
        const newBotVersion = get().getNewBotVersion();
        const botMappieNewVersion = transformBotToMappieVersion(
            {
                ...newBotVersion,
                botId: bot.botId,
                botVersionId: bot.botVersionId,
                labels: bot.labels,
            },
            isProgrammaticBot(bot.labels)
                ? undefined
                : get().customModuleNodeHandles,
        ) as BotMappieNewVersion;

        if (isProgrammaticBot(bot.labels)) {
            botMappieNewVersion.dialog = get().mappieBot.dialog;
            botMappieNewVersion.globals = get().mappieBot.globals;
            botMappieNewVersion.prompts = get().mappieBot.prompts;
        }

        return botMappieNewVersion;
    },

    markSaveSuccess: () => {
        set((state) => {
            state.errorSaveBotVersion = undefined;
            state.changesInBotVersion = false;
            resetUndoStack(state, get);
        });
    },

    saveBotVersion: async (accountId?: string): Promise<string> => {
        const bot = get().editingBot;

        if (isCustomCxdBot(bot.labels)) {
            throw new Error(
                'Custom-cxd bots must be saved from the flow editor. Use Save in the DFE toolbar.',
            );
        }

        const newBotVersion = get().getNewBotVersion();

        try {
            const invalidNodes = bot.nodesReactFlow.filter(
                (node) => node.data && node.data.valid === false,
            );

            if (invalidNodes.length > 0) {
                const invalidNodeTypes = invalidNodes
                    .map((node) => node.type)
                    .join(', ');
                throw new Error(
                    `Invalid node configuration in: ${invalidNodeTypes}`,
                );
            }
            const bS = botSchema(t);
            await bS.validate(newBotVersion, {
                abortEarly: false,
            });

            const botMappieNewVersion = get().getMappiePayloadForNewVersion();

            await patchBotVersion(
                bot.botId,
                bot.botVersionId,
                botMappieNewVersion,
                accountId,
            );

            get().markSaveSuccess();

            return Promise.resolve('success');
        } catch (e) {
            let errorMessage: string = '';
            if (e instanceof yup.ValidationError) {
                errorMessage =
                    e?.inner
                        .map((e) => `${e.path} - ${e.message}`)
                        .reduce((a, v) => `${a} ${v}`) ?? '';
            } else if (e instanceof Error) {
                errorMessage = e.message;
            }

            set((state) => {
                state.errorSaveBotVersion = {
                    isErrorSaveBotVersion: true,
                    errorMessageSaveBot: errorMessage,
                    errorMessageSaveBotKey: `get_bot_version_${bot.botId}_${bot.botVersionId}`,
                };
            });

            throw e;
        }
    },
});
